import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { phasesToFills } from "@/lib/positionMath";

function toLocalDatetime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDatetime(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d)) return null;
  return d.toISOString();
}

// True FIFO lot-queue reconciliation across fills.
// Each opening fill creates a lot; closing fills peel shares off the oldest
// lot(s) first. Per-row P/L% is computed against the blended cost basis of the
// specific lots consumed by that row (not a running average).
function computeMetrics(rows, direction) {
  const isLong = direction !== "short";
  let lots = []; // [{shares, price}]
  let lotSide = isLong ? "long" : "short";
  let realized = 0;
  let totalCapital = 0;
  let exitNotional = 0;
  let exitShares = 0;
  let opened = null;
  let closed = null;
  const perRow = rows.map((r) => {
    const sh = parseFloat(r.shares);
    const pr = parseFloat(r.price);
    if (!sh || !pr || sh <= 0 || pr <= 0) return { rowPct: null, rowPnl: null };
    // Fresh - opens
    if (lots.length === 0) {
      lotSide = r.side === "BUY" ? "long" : "short";
      lots.push({ shares: sh, price: pr });
      totalCapital += pr * sh;
      if (!opened && r.at) opened = r.at;
      return { rowPct: null, rowPnl: null };
    }
    const opening = (lotSide === "long" && r.side === "BUY") || (lotSide === "short" && r.side === "SELL");
    if (opening) {
      lots.push({ shares: sh, price: pr });
      totalCapital += pr * sh;
      if (!opened && r.at) opened = r.at;
      return { rowPct: null, rowPnl: null };
    }
    // Closing: peel from head
    let remainingToClose = sh;
    let rowPnl = 0;
    let rowBasisCost = 0;
    let rowClosedShares = 0;
    while (remainingToClose > 1e-9 && lots.length > 0) {
      const lot = lots[0];
      const consume = Math.min(lot.shares, remainingToClose);
      const pnlPer = lotSide === "long" ? (pr - lot.price) : (lot.price - pr);
      rowPnl += pnlPer * consume;
      rowBasisCost += lot.price * consume;
      rowClosedShares += consume;
      exitNotional += pr * consume;
      exitShares += consume;
      lot.shares -= consume;
      remainingToClose -= consume;
      if (lot.shares < 1e-9) lots.shift();
    }
    realized += rowPnl;
    if (r.at) closed = r.at;
    // Position flip: any remainder opens a fresh lot the other way
    if (remainingToClose > 1e-9) {
      lotSide = r.side === "BUY" ? "long" : "short";
      lots = [{ shares: remainingToClose, price: pr }];
      totalCapital += pr * remainingToClose;
    }
    // Row % against the blended basis of the specific lots this row consumed.
    let rowPct = null;
    if (rowClosedShares > 0 && rowBasisCost > 0) {
      const blendedBasis = rowBasisCost / rowClosedShares;
      rowPct = ((rowPnl / rowClosedShares) / blendedBasis) * 100;
    }
    return { rowPct, rowPnl };
  });
  const totalOpen = lots.reduce((a, l) => a + l.shares, 0);
  const remaining = lotSide === "long" ? totalOpen : -totalOpen;
  const weightedPct = totalCapital > 0 ? (realized / totalCapital) * 100 : null;
  const exitAvg = exitShares > 0 ? exitNotional / exitShares : null;
  const duration = (opened && closed) ? (new Date(closed) - new Date(opened)) : null;
  return { perRow, realized, weightedPct, remaining, totalCapital, exitAvg, duration };
}

function fmtDuration(ms) {
  if (!ms || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins || (!days && !hours)) parts.push(`${mins}m`);
  return parts.join(" ");
}

const nowLocal = () => toLocalDatetime(new Date().toISOString());

export default function CloseTradeDialog({ open, onOpenChange, trade, onClosed }) {
  const defaultSide = trade?.direction === "short" ? "BUY" : "SELL";
  const buildInitialRows = () => {
    // Pre-populate rows from existing phase fills (Open - Entry/Added/Trimmed)
    const fills = phasesToFills(trade?.phases || [], trade?.direction);
    if (fills.length === 0) {
      return [{ side: defaultSide, shares: "", price: "", at: nowLocal() }];
    }
    return fills.map((f) => ({
      side: f.side,
      shares: String(f.shares),
      price: String(f.price),
      at: toLocalDatetime(f.at) || nowLocal(),
    }));
  };
  const [rows, setRows] = useState(buildInitialRows);
  const [submitting, setSubmitting] = useState(false);

  // When the dialog opens, refresh rows from the latest trade snapshot.
  useEffect(() => {
    if (open) setRows(buildInitialRows());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trade?.id, trade?.phases?.length]);

  const metrics = useMemo(() => computeMetrics(rows, trade?.direction), [rows, trade?.direction]);

  const addRow = () => setRows((r) => [...r, { side: defaultSide, shares: "", price: "", at: nowLocal() }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const remaining = metrics.remaining;
  const remainingClose = Math.abs(remaining) < 1e-6;
  const hasIncompleteRow = rows.some((r) => !r.shares || !r.price || !r.at);
  const canSubmit = !submitting && rows.length > 0 && !hasIncompleteRow && remainingClose;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        fills: rows.map((r) => ({
          side: r.side,
          shares: parseFloat(r.shares),
          price: parseFloat(r.price),
          at: fromLocalDatetime(r.at),
        })),
      };
      await api.post(`/trades/${trade.id}/close`, payload);
      toast.success("Trade closed");
      onClosed?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close trade");
    } finally {
      setSubmitting(false);
    }
  };

  const pnlTone = metrics.realized > 0 ? "text-emerald-400" : metrics.realized < 0 ? "text-rose-400" : "text-zinc-200";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 rounded-sm max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="close-trade-dialog">
        <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
          <XCircle className="w-4 h-4 text-blue-400" />
          Close Trade <span className="text-zinc-500 font-mono text-sm">· {trade?.ticker}</span>
        </DialogTitle>
        <p className="text-xs text-zinc-500 -mt-1">Enter each broker fill. Shares Remaining must be zero to submit. P/L rows use FIFO cost basis — each sell is matched against the oldest buy lots first.</p>

        <div className="overflow-y-auto -mx-6 px-6 mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                <th className="text-left font-mono py-2 font-normal w-24">Side</th>
                <th className="text-right font-mono py-2 font-normal">Shares</th>
                <th className="text-right font-mono py-2 font-normal">Price</th>
                <th className="text-left font-mono py-2 font-normal">Date / Time</th>
                <th className="text-right font-mono py-2 font-normal">P/L %</th>
                <th className="py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rowMetric = metrics.perRow[i];
                const pctTone = rowMetric?.rowPct == null
                  ? "text-zinc-600"
                  : rowMetric.rowPct >= 0 ? "text-emerald-400" : "text-rose-400";
                return (
                  <tr key={i} className="border-b border-zinc-900/60 align-top" data-testid={`close-fill-row-${i}`}>
                    <td className="py-2 pr-2">
                      <Select value={r.side} onValueChange={(v) => updateRow(i, { side: v })}>
                        <SelectTrigger data-testid={`close-fill-side-${i}`} className="h-8 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800 rounded-sm">
                          <SelectItem value="BUY" data-testid={`close-fill-side-buy-${i}`}>
                            <span className="text-emerald-400 font-mono">BUY</span>
                          </SelectItem>
                          <SelectItem value="SELL" data-testid={`close-fill-side-sell-${i}`}>
                            <span className="text-rose-400 font-mono">SELL</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={r.shares}
                        onChange={(e) => updateRow(i, { shares: e.target.value })}
                        data-testid={`close-fill-shares-${i}`}
                        className="h-8 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={r.price}
                        onChange={(e) => updateRow(i, { price: e.target.value })}
                        data-testid={`close-fill-price-${i}`}
                        className="h-8 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="datetime-local"
                        value={r.at}
                        onChange={(e) => updateRow(i, { at: e.target.value })}
                        data-testid={`close-fill-at-${i}`}
                        className="h-8 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-xs"
                      />
                    </td>
                    <td className={`py-2 pr-2 text-right font-mono ${pctTone}`} data-testid={`close-fill-pct-${i}`}>
                      {rowMetric?.rowPct == null ? "—" : `${rowMetric.rowPct.toFixed(2)}%`}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => removeRow(i)}
                        disabled={rows.length === 1}
                        title="Remove row"
                        data-testid={`close-fill-remove-${i}`}
                        className="text-zinc-500 hover:text-rose-400 disabled:opacity-30"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button
            onClick={addRow}
            data-testid="close-add-row"
            className="mt-2 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-blue-400 hover:text-blue-300"
          >
            <Plus className="w-3 h-3" /> Add fill
          </button>
        </div>

        <div className="mt-4 border-t border-zinc-900 pt-3 grid grid-cols-4 gap-3 text-xs font-mono">
          <div>
            <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Total P&amp;L</div>
            <div className={`text-lg mt-0.5 ${pnlTone}`} data-testid="close-total-pnl">
              {metrics.totalCapital ? `$${metrics.realized.toFixed(2)}` : "—"}
            </div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Realized Return %</div>
            <div className={`text-lg mt-0.5 ${pnlTone}`} data-testid="close-weighted-pct">
              {metrics.weightedPct == null ? "—" : `${metrics.weightedPct.toFixed(2)}%`}
            </div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Duration</div>
            <div className="text-lg mt-0.5 text-zinc-200" data-testid="close-duration">
              {fmtDuration(metrics.duration)}
            </div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Shares Remaining</div>
            <div className={`text-lg mt-0.5 ${remainingClose ? "text-emerald-400" : "text-amber-400"}`} data-testid="close-remaining">
              {remaining === 0 ? "0" : (Math.abs(remaining) < 1e-6 ? "0" : remaining.toFixed(4))}
            </div>
          </div>
        </div>

        {!remainingClose && rows.some((r) => r.shares && r.price) && (
          <div className="mt-2 text-[11px] text-amber-400 font-mono" data-testid="close-remaining-warning">
            Position not fully closed. Add fills so BUYs and SELLs net to zero shares before submitting.
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={() => onOpenChange(false)} data-testid="close-cancel" className="rounded-sm bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 h-9">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="close-submit" className="rounded-sm bg-blue-600 hover:bg-blue-500 h-9 disabled:opacity-50">
            {submitting ? "Closing…" : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
